import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { ChevronRight, Wrench, Lightbulb, Heart, AlertTriangle, Flag, Paperclip } from 'lucide-react';
import { TicketStatusBadge } from './TicketStatusBadge';
import { SupportTicket } from '@/hooks/useSupportTickets';

interface SupportTicketCardProps {
  ticket: SupportTicket;
  onClick: () => void;
}

const typeIcons = {
  request: Wrench,
  suggestion: Lightbulb,
  compliment: Heart,
  complaint: AlertTriangle,
  report: Flag,
};

const typeColors = {
  request: 'text-blue-500',
  suggestion: 'text-purple-500',
  compliment: 'text-green-500',
  complaint: 'text-red-500',
  report: 'text-red-600',
};

export const SupportTicketCard = ({ ticket, onClick }: SupportTicketCardProps) => {
  const Icon = typeIcons[ticket.type];
  const colorClass = typeColors[ticket.type];
  
  const timeAgo = formatDistanceToNow(new Date(ticket.created_at), {
    addSuffix: true,
    locale: ptBR,
  });

  return (
    <button
      onClick={onClick}
      className="w-full text-left bg-black/40 hover:bg-black/60 border border-white/10 hover:border-white/20 rounded-lg p-4 transition-all animate-fade-in"
    >
      <div className="flex items-start gap-3">
        <div className={`w-10 h-10 rounded-full bg-white/5 border border-white/10 flex items-center justify-center flex-shrink-0 ${colorClass}`}>
          <Icon className="w-5 h-5" />
        </div>
        
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2 mb-1">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-xs text-white/40 font-mono">
                  {ticket.ticket_number}
                </span>
                <TicketStatusBadge status={ticket.status} />
              </div>
              <h3 className="font-semibold text-sm md:text-base line-clamp-1 text-white">
                {ticket.subject}
              </h3>
            </div>
            <ChevronRight className="w-5 h-5 text-white/40 flex-shrink-0" />
          </div>
          
          <p className="text-sm text-white/60 line-clamp-2 mb-2">
            {ticket.description}
          </p>
          
          <div className="flex items-center gap-3 text-xs text-white/40">
            <span>{timeAgo}</span>
            {ticket.attachments && ticket.attachments.length > 0 && (
              <span className="flex items-center gap-1">
                <Paperclip className="w-3 h-3" />
                {ticket.attachments.length}
              </span>
            )}
          </div>
        </div>
      </div>
    </button>
  );
};
